/**
 * Local Supabase is configured with VITE_SUPABASE_URL (often http://127.0.0.1:55421).
 * When testing the Vite dev server from a phone on LAN, asset URLs must use the PC's
 * Wi‑Fi IP with the same Supabase port — 127.0.0.1 on the phone points at the phone itself.
 */

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

function parseOrigin(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/**
 * Supabase API origin for the current browser context.
 * In dev on LAN (phone testing), swaps 127.0.0.1 → window.location.hostname, same port.
 */
export function getSupabaseOrigin(): string {
  const configured = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  if (!configured) return '';

  if (!import.meta.env.DEV || typeof window === 'undefined') {
    return parseOrigin(configured) ?? configured;
  }

  const pageHost = window.location.hostname;
  if (LOCAL_HOSTS.has(pageHost)) {
    return parseOrigin(configured) ?? configured;
  }

  try {
    const configuredUrl = new URL(configured);
    configuredUrl.hostname = pageHost;
    return configuredUrl.origin;
  } catch {
    return parseOrigin(configured) ?? configured;
  }
}

/**
 * Rewrite a baked-in Supabase public URL to the runtime origin (LAN dev / phone testing).
 */
export function resolveSupabasePublicUrl(url: string): string {
  if (!url || typeof url !== 'string') return url;

  const configuredOrigin = parseOrigin(import.meta.env.VITE_SUPABASE_URL as string);
  const runtimeOrigin = getSupabaseOrigin();

  if (!configuredOrigin || !runtimeOrigin || configuredOrigin === runtimeOrigin) {
    return url;
  }

  if (url.startsWith(configuredOrigin)) {
    return runtimeOrigin + url.slice(configuredOrigin.length);
  }

  return url;
}
